import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CATEGORY_ICONS } from './constants';
import { displayedPreviewIndex, nextPreviewIndex } from './nextPreviewIndex';
import {
    categorizeAssetWithType,
    getAssetPreviewType,
    hasCbctSources,
    isPreviewableAsset
} from './fileTypes';
import type {
    Asset,
    FileCategory,
    FileCategoryConfig,
    FilesApi,
    LayoutMode,
    ListMode,
    ResolvedFilesCapabilities,
    ViewMode
} from './types';
import {
    assetPreviewId,
    formatCreatedDate,
    readStoredLayoutMode,
    readStoredListMode,
    sortAssetsByDate
} from './utils';
import { useCbctProcessingJobs } from './useCbctProcessing';
import { useFileDeleteRename } from './useFileDeleteRename';
import { useFileDownload } from './useFileDownload';
import { useFileSelection } from './useFileSelection';
import { useFileUpload } from './useFileUpload';
import { useThumbnails } from './useThumbnails';

const CATEGORY_ORDER = ['Images', 'Videos', 'Documents', '3D Models', 'CBCT', 'Others'] as const;

// Built-in taxonomy, used whenever the host doesn't supply its own
// `categoryConfig` - keeps every existing caller (patient files) on exactly
// the behavior it had before `FileCategoryConfig` existed.
const DEFAULT_CATEGORY_CONFIG: FileCategoryConfig = {
    order: CATEGORY_ORDER,
    categorize: categorizeAssetWithType,
    icons: CATEGORY_ICONS,
    previewable: { Images: true }
};

export interface UseFilesManagerOptions {
    patientId: string;
    /** Names the selected-files zip (patient name + date) when provided. */
    patientName?: string;
    assets: Record<string, Asset>;
    refreshAssets: () => Promise<void>;
    api: FilesApi;
    capabilities: ResolvedFilesCapabilities;
    /**
     * Controlled preview contract:
     * - `undefined` → UNCONTROLLED. Previews are purely local state; the hook
     *   never reconciles against this prop (work-queue task hosts).
     * - `null` → controlled parent asking to close (URL back).
     * - `string[]` → controlled parent asking to open these file ids.
     */
    initialPreviewFileIds?: string[] | null;
    onPreviewChange?: (fileIds: string[] | null) => void;
    /** localStorage key prefix for layout/list mode persistence. */
    storageKeyPrefix?: string;
    /** Overrides the "By category" grouping. Defaults to the built-in
     *  extension/storageCategory taxonomy - see `FileCategoryConfig`. */
    categoryConfig?: FileCategoryConfig;
}

export function useFilesManager({
    patientId,
    patientName,
    assets,
    refreshAssets,
    api,
    capabilities,
    initialPreviewFileIds,
    onPreviewChange,
    storageKeyPrefix = 'files',
    categoryConfig = DEFAULT_CATEGORY_CONFIG
}: UseFilesManagerOptions) {
    const layoutModeKey = `${storageKeyPrefix}_layout_mode`;
    const listModeKey = `${storageKeyPrefix}_list_mode`;

    const [searchTerm, setSearchTerm] = useState('');
    const [listMode, setListMode] = useState<ListMode>(() => readStoredListMode(listModeKey));
    const [layoutMode, setLayoutMode] = useState<LayoutMode>(() =>
        readStoredLayoutMode(layoutModeKey)
    );
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    // Set when a CBCT Pano/Ceph PNG is saved during preview. We defer the asset
    // refresh until the user returns to the list (see goBackToList) so the save
    // doesn't shift previewIndex and swap the viewer out mid-session.
    const cbctExportedRef = useRef(false);
    const [selectedPreviewIndex, setPreviewIndex] = useState<number | null>(null);
    // assetPreviewId of the file on screen, so a list refresh can't swap it for a neighbour.
    const [shownPreviewId, setShownPreviewId] = useState<string | null>(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    // Cache of signed URLs by assetId so navigating back/forward is instant.
    // Plain state - updates use the functional setState form so reads inside
    // async handlers see the latest value without stale-closure tricks.
    const [previewUrlCache, setPreviewUrlCache] = useState<Map<string, string>>(() => new Map());
    const [previewError, setPreviewError] = useState<string | null>(null);
    // Download warning (ConfirmModal) for large ZIPs.
    const [pendingDownload, setPendingDownload] = useState<{
        message: string;
        action: () => Promise<void>;
    } | null>(null);

    const updateLayoutMode = useCallback(
        (mode: LayoutMode) => {
            setLayoutMode(mode);
            localStorage.setItem(layoutModeKey, mode);
        },
        [layoutModeKey]
    );

    const updateListMode = useCallback(
        (mode: ListMode) => {
            setListMode(mode);
            localStorage.setItem(listModeKey, mode);
        },
        [listModeKey]
    );

    // --- Derived data ---

    const fileCategories = useMemo<FileCategory[]>(() => {
        const categories: Record<string, Asset[]> = {};
        categoryConfig.order.forEach((label) => {
            categories[label] = [];
        });
        const term = searchTerm.trim().toLowerCase();
        const filteredAssets = Object.values(assets).filter(
            (asset) => !term || asset.name.toLowerCase().includes(term)
        );
        filteredAssets.forEach((asset) => {
            const category = categoryConfig.categorize(asset);
            (categories[category] ??= []).push(asset);
        });
        return categoryConfig.order
            .map((label) => ({
                label,
                assets: sortAssetsByDate(categories[label] ?? []),
                icon: categoryConfig.icons[label],
                // Falls back to the pre-existing `label === 'Images'` check
                // when the host's categoryConfig doesn't (yet) supply a
                // `previewable` map at all - see FileCategoryConfig.previewable.
                previewable: categoryConfig.previewable?.[label] ?? label === 'Images'
            }))
            .filter((cat) => cat.assets.length > 0);
    }, [assets, searchTerm, categoryConfig]);

    const dateViewAssets = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        const filtered = Object.values(assets).filter(
            (asset) => !term || asset.name.toLowerCase().includes(term)
        );
        return sortAssetsByDate(filtered);
    }, [assets, searchTerm]);

    const dateGroups = useMemo(() => {
        const groups: Array<{ label: string; assets: Asset[] }> = [];
        let currentLabel = '';
        for (const asset of dateViewAssets) {
            const label = formatCreatedDate(asset.createdAt) || 'Unknown date';
            if (label !== currentLabel) {
                currentLabel = label;
                groups.push({ label, assets: [asset] });
            } else {
                groups[groups.length - 1].assets.push(asset);
            }
        }
        return groups;
    }, [dateViewAssets]);

    const visibleAssets = useMemo(() => {
        if (listMode === 'category') return fileCategories.flatMap((c) => c.assets);
        return dateViewAssets;
    }, [listMode, fileCategories, dateViewAssets]);

    const totalFiles = useMemo(() => Object.keys(assets).length, [assets]);

    const filteredFilesCount = useMemo(
        () => fileCategories.reduce((sum, cat) => sum + cat.assets.length, 0),
        [fileCategories]
    );

    const filesCountText = useMemo(() => {
        const count = searchTerm ? filteredFilesCount : totalFiles;
        const fileWord = count === 1 ? 'file' : 'files';
        const categoryText = fileCategories.length === 1 ? 'category' : 'categories';
        if (searchTerm && filteredFilesCount === 0)
            return `No files found matching "${searchTerm}"`;
        if (searchTerm)
            return `Found ${count} ${fileWord} in ${fileCategories.length} ${categoryText}`;
        return `${count} ${fileWord} in ${fileCategories.length} ${categoryText}`;
    }, [totalFiles, filteredFilesCount, fileCategories.length, searchTerm]);

    // --- Composed hooks ---

    const upload = useFileUpload({
        patientId,
        refreshAssets,
        api,
        onUploadError: capabilities.onUploadError
    });

    const selection = useFileSelection({ visibleAssets, searchTerm });
    const { selectedAssets, exitSelection, setIsDownloadingSelected } = selection;

    const download = useFileDownload({ patientId, api, zipBaseName: patientName });
    const {
        getSignedUrl,
        handleDownloadCategory,
        handleDownloadSelected: downloadSelected,
        shouldWarnBeforeDownload
    } = download;

    const deleteRename = useFileDeleteRename({ patientId, refreshAssets, exitSelection, api });

    const thumbnails = useThumbnails({ patientId, assets, layoutMode, api });

    // Inert when the host doesn't supply the FilesApi.cbctProcessing capability.
    const cbctProcessing = useCbctProcessingJobs({ api: api.cbctProcessing, refreshAssets });
    const previewOptions = useMemo(
        () => ({ cbctProcessing: cbctProcessing.enabled }),
        [cbctProcessing.enabled]
    );

    // --- Category expansion ---

    const toggleCategory = useCallback((label: string) => {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(label)) next.delete(label);
            else next.add(label);
            return next;
        });
    }, []);

    // --- Preview model ---

    // Flat, date-sorted list of all previewable assets (respects current search).
    // This is the navigation pool for prev/next inside the preview view.
    const previewableAssets = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        const filtered = Object.values(assets).filter((a) => {
            if (term && !a.name.toLowerCase().includes(term)) return false;
            return isPreviewableAsset(a, previewOptions);
        });
        return sortAssetsByDate(filtered);
    }, [assets, searchTerm, previewOptions]);

    const previewIndex = displayedPreviewIndex(
        previewableAssets,
        shownPreviewId,
        selectedPreviewIndex
    );
    const currentPreviewAsset =
        previewIndex !== null ? (previewableAssets[previewIndex] ?? null) : null;
    const currentPreviewType = currentPreviewAsset
        ? getAssetPreviewType(currentPreviewAsset, previewOptions)
        : null;
    // Re-derives on cache fills via normal state-driven re-render - no version
    // counter trick needed when the cache lives in state.
    const currentPreviewUrl = currentPreviewAsset
        ? (previewUrlCache.get(String(currentPreviewAsset.assetId)) ?? null)
        : null;

    const ensurePreviewUrl = useCallback(
        async (asset: Asset): Promise<string | null> => {
            const key = String(asset.assetId);
            const cached = previewUrlCache.get(key);
            if (cached) return cached;
            try {
                const url = await getSignedUrl(asset);
                // Functional setState - copies the previous Map and adds the new
                // entry so the update is immutable and React schedules a re-render.
                setPreviewUrlCache((prev) => new Map(prev).set(key, url));
                return url;
            } catch {
                return null;
            }
        },
        [getSignedUrl, previewUrlCache]
    );

    const openPreviewAtIndex = useCallback(
        async (index: number) => {
            const asset = previewableAssets[index];
            if (!asset) return;
            setPreviewError(null);
            setPreviewIndex(index);
            setShownPreviewId(assetPreviewId(asset));
            setViewMode('preview');
            onPreviewChange?.([assetPreviewId(asset)]);
            const key = String(asset.assetId);
            if (!previewUrlCache.has(key)) {
                setIsLoadingPreview(true);
                const url = await ensurePreviewUrl(asset);
                setIsLoadingPreview(false);
                if (!url) setPreviewError(`Failed to load preview for "${asset.name}".`);
            }
        },
        [previewableAssets, previewUrlCache, ensurePreviewUrl, onPreviewChange]
    );

    const handlePreviewFile = useCallback(
        async (asset: Asset) => {
            if (!isPreviewableAsset(asset, previewOptions)) return;
            const idx = previewableAssets.findIndex((a) => a.assetId === asset.assetId);
            if (idx === -1) return;
            await openPreviewAtIndex(idx);
        },
        [previewableAssets, openPreviewAtIndex, previewOptions]
    );

    const handlePreviewCategory = useCallback(
        async (category: FileCategory) => {
            if (!category.previewable) return;
            const firstImage = category.assets[0];
            if (!firstImage) return;
            const idx = previewableAssets.findIndex((a) => a.assetId === firstImage.assetId);
            if (idx === -1) return;
            await openPreviewAtIndex(idx);
        },
        [previewableAssets, openPreviewAtIndex]
    );

    const navigatePreview = useCallback(
        (delta: number) => {
            if (!capabilities.enablePreviewNavigation) return;
            if (previewIndex === null) return;
            const next = previewIndex + delta;
            if (next < 0 || next >= previewableAssets.length) return;
            void openPreviewAtIndex(next);
        },
        [
            capabilities.enablePreviewNavigation,
            previewIndex,
            previewableAssets.length,
            openPreviewAtIndex
        ]
    );

    const goBackToList = useCallback(() => {
        setViewMode('list');
        setPreviewIndex(null);
        setShownPreviewId(null);
        setPreviewError(null);
        onPreviewChange?.(null);
        if (cbctExportedRef.current) {
            cbctExportedRef.current = false;
            void refreshAssets();
        }
    }, [refreshAssets, onPreviewChange]);

    /** Defer the asset refresh caused by a CBCT export until the user returns
     *  to the list, so the save doesn't swap the viewer out mid-session. */
    const notifyCbctExported = useCallback(() => {
        cbctExportedRef.current = true;
    }, []);

    useEffect(() => {
        // Uncontrolled usage (work-queue tasks pass no preview props): previews are
        // purely local state, so never reconcile against `initialPreviewFileIds` -
        // treating the absent prop as "no preview" would close every preview as it
        // opens. `null` still means a controlled parent asking to close (URL back).
        if (initialPreviewFileIds === undefined) return;
        if (previewableAssets.length === 0) return; // wait until assets can be matched
        const matches = (a: Asset, id: string) =>
            a.patientFileId === id || String(a.assetId) === id;

        const currentIds =
            viewMode !== 'preview'
                ? null
                : previewIndex !== null && previewableAssets[previewIndex]
                  ? [assetPreviewId(previewableAssets[previewIndex])]
                  : null;
        // Multi-id requests (legacy multi-STL preview URLs) collapse to their
        // first matched file - the module only previews one asset at a time.
        const desired =
            initialPreviewFileIds && initialPreviewFileIds.length > 0
                ? initialPreviewFileIds
                : null;

        const sameIds = (a: string[] | null, b: string[] | null) =>
            (a === null && b === null) ||
            (!!a && !!b && a.length === b.length && a.every((v, i) => v === b[i]));
        if (sameIds(currentIds, desired ? desired.slice(0, 1) : null)) return;

        if (!desired) {
            if (viewMode === 'preview') goBackToList();
            return;
        }

        const idx = previewableAssets.findIndex((a) => matches(a, desired[0]));
        if (idx === -1) return;
        void openPreviewAtIndex(idx);
    }, [
        initialPreviewFileIds,
        previewableAssets,
        viewMode,
        previewIndex,
        goBackToList,
        openPreviewAtIndex
    ]);

    // Keyboard shortcuts for the preview view. Left/Right only when navigation
    // is enabled; Escape closes unless a modal opened from the preview owns it.
    const isModalOpen = deleteRename.deleteTargets.length > 0 || deleteRename.renameTarget !== null;
    useEffect(() => {
        if (viewMode !== 'preview' || isModalOpen) return;
        const navEnabled = capabilities.enablePreviewNavigation;
        const onKey = (e: KeyboardEvent) => {
            if (navEnabled && e.key === 'ArrowLeft') {
                e.preventDefault();
                navigatePreview(-1);
            } else if (navEnabled && e.key === 'ArrowRight') {
                e.preventDefault();
                navigatePreview(1);
            } else if (e.key === 'Escape') {
                e.preventDefault();
                goBackToList();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [
        viewMode,
        isModalOpen,
        capabilities.enablePreviewNavigation,
        navigatePreview,
        goBackToList
    ]);

    // Keep the preview on the file it shows when the list changes under it.
    useEffect(() => {
        const change = nextPreviewIndex(previewableAssets, shownPreviewId, selectedPreviewIndex);
        if (change.kind === 'close') goBackToList();
        else if (change.kind === 'follow') setPreviewIndex(change.index);
        else if (change.kind === 'open') void openPreviewAtIndex(change.index);
    }, [previewableAssets, shownPreviewId, selectedPreviewIndex, goBackToList, openPreviewAtIndex]);

    // --- CBCT preview sources ---
    // A CBCT "file" is really two GCS objects - the manifest.json and volume.bin
    // the processing pipeline wrote back - referenced by their own
    // patientFileIds in this file's metadata.

    const [cbctSources, setCbctSources] = useState<
        Record<string, { manifestUrl: string; volumeUrl?: string }>
    >({});

    useEffect(() => {
        if (currentPreviewType !== 'cbct' || !currentPreviewAsset || !patientId) return;
        const key = String(currentPreviewAsset.assetId);
        if (cbctSources[key]) return;

        const meta = currentPreviewAsset.metadata ?? null;
        const manifestId = meta && typeof meta.manifest === 'string' ? meta.manifest : null;
        const binaryId = meta && typeof meta.binary === 'string' ? meta.binary : null;
        if (!manifestId) {
            // With the processing capability, a missing manifest means "not
            // processed yet" - the CbctProcessingGate handles it, not an error.
            if (!cbctProcessing.enabled) {
                setPreviewError(
                    `"${currentPreviewAsset.name}" is missing its CBCT manifest reference.`
                );
            }
            return;
        }

        let alive = true;
        void (async () => {
            try {
                const [manifestUrl, volumeUrl] = await Promise.all([
                    api.getDownloadUrl(patientId, manifestId),
                    binaryId ? api.getDownloadUrl(patientId, binaryId) : Promise.resolve(undefined)
                ]);
                if (alive)
                    setCbctSources((prev) => ({ ...prev, [key]: { manifestUrl, volumeUrl } }));
            } catch {
                if (alive)
                    setPreviewError(`Failed to load CBCT scan for "${currentPreviewAsset.name}".`);
            }
        })();
        return () => {
            alive = false;
        };
    }, [
        currentPreviewType,
        currentPreviewAsset,
        patientId,
        cbctSources,
        api,
        cbctProcessing.enabled
    ]);

    // Previewing an unprocessed CBCT: check for an already-running job so the
    // gate reflects server state (and picks up jobs started in another tab).
    const watchCbct = cbctProcessing.watch;
    useEffect(() => {
        if (!cbctProcessing.enabled) return;
        if (currentPreviewType !== 'cbct' || !currentPreviewAsset) return;
        if (hasCbctSources(currentPreviewAsset)) return;
        void watchCbct(currentPreviewAsset);
    }, [cbctProcessing.enabled, currentPreviewType, currentPreviewAsset, watchCbct]);

    // --- Download / delete orchestration ---

    const onDownloadSelected = useCallback(async () => {
        const doDownload = async () => {
            setIsDownloadingSelected(true);
            await downloadSelected(selectedAssets, assets, () => {
                setIsDownloadingSelected(false);
                exitSelection();
            });
        };
        if (shouldWarnBeforeDownload(selectedAssets.size)) {
            setPendingDownload({
                message: `You are about to download ${selectedAssets.size} files as a ZIP. This may use significant memory.`,
                action: doDownload
            });
            return;
        }
        await doDownload();
    }, [
        selectedAssets,
        assets,
        shouldWarnBeforeDownload,
        setIsDownloadingSelected,
        downloadSelected,
        exitSelection
    ]);

    const onDownloadCategory = useCallback(
        async (category: FileCategory) => {
            const doDownload = async () => {
                await handleDownloadCategory(category);
            };
            if (shouldWarnBeforeDownload(category.assets.length)) {
                setPendingDownload({
                    message: `You are about to download ${category.assets.length} files as a ZIP. This may use significant memory.`,
                    action: doDownload
                });
                return;
            }
            await doDownload();
        },
        [shouldWarnBeforeDownload, handleDownloadCategory]
    );

    const confirmPendingDownload = useCallback(async () => {
        if (!pendingDownload) return;
        const action = pendingDownload.action;
        setPendingDownload(null);
        await action();
    }, [pendingDownload]);

    const cancelPendingDownload = useCallback(() => {
        setPendingDownload(null);
    }, []);

    const handleDeleteSelected = useCallback(() => {
        deleteRename.handleDeleteSelected(assets, selectedAssets);
    }, [deleteRename, assets, selectedAssets]);

    const hasGcsSelected = useMemo(() => {
        return Object.values(assets).some(
            (a) => selectedAssets.has(String(a.assetId)) && a.source === 'gcs' && a.patientFileId
        );
    }, [assets, selectedAssets]);

    return {
        // Search / layout
        searchTerm,
        setSearchTerm,
        listMode,
        layoutMode,
        updateLayoutMode,
        updateListMode,
        expandedCategories,
        toggleCategory,

        // Derived data
        fileCategories,
        dateViewAssets,
        dateGroups,
        visibleAssets,
        totalFiles,
        filteredFilesCount,
        filesCountText,

        // View / preview
        viewMode,
        previewableAssets,
        previewIndex,
        currentPreviewAsset,
        currentPreviewType,
        currentPreviewUrl,
        previewUrlCache,
        isLoadingPreview,
        previewError,
        setPreviewError,
        cbctSources,
        handlePreviewFile,
        handlePreviewCategory,
        navigatePreview,
        goBackToList,
        notifyCbctExported,

        // Selection
        selection,

        // Download
        download,
        onDownloadSelected,
        onDownloadCategory,
        pendingDownload,
        confirmPendingDownload,
        cancelPendingDownload,

        // Delete / rename
        deleteRename,
        handleDeleteSelected,

        // Upload
        upload,

        // Thumbnails
        thumbnails,

        // CBCT processing (inert unless FilesApi.cbctProcessing is supplied)
        cbctProcessing,

        // Selection-derived
        hasGcsSelected
    };
}

export type FilesManager = ReturnType<typeof useFilesManager>;
